import { useCallback, useEffect, useMemo, useState } from 'react';
import { Annotation } from '../types';
import { fetchAnnotations, getLocalStorageItemId } from '../utils/api';

export type AnnotationsHookResult = {
  annotations: Annotation[];
  updateAnnotation: (annotation: Annotation) => Annotation;
  removeAnnotation: (annotation: Annotation) => void;
  addAnnotation: (annotation: Annotation) => Annotation;
};

interface UseAnnotationsArgs {
  isbn: string;
  onAnnotationsLoaded?: () => void;
}

export default function useAnnotations({
  isbn,
  onAnnotationsLoaded,
}: UseAnnotationsArgs): AnnotationsHookResult {
  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Load annotations when product meta or isbn changes
  useEffect(() => {
    setIsLoading(true);
    fetchAnnotations(isbn).then((fetchedAnnotations) => {
      setAnnotations(fetchedAnnotations);
      setIsLoading(false);

      onAnnotationsLoaded?.();
    });
  }, [isbn, onAnnotationsLoaded]);

  // Save annotations to localStorage until we have a proper API
  useEffect(() => {
    if (isLoading) {
      return;
    }
    localStorage.setItem(
      getLocalStorageItemId(isbn),
      JSON.stringify(annotations),
    );
  }, [annotations, isbn, isLoading]);

  const addAnnotation = useCallback((annotation: Annotation): Annotation => {
    setAnnotations((prev) => [...prev, annotation]);
    return annotation;
  }, []);

  const updateAnnotation = useCallback((annotation: Annotation): Annotation => {
    setAnnotations((prev) =>
      prev.map((a) => (a.id === annotation.id ? annotation : a)),
    );
    return annotation;
  }, []);

  const removeAnnotation = useCallback((annotation: Annotation) => {
    setAnnotations((prev) => prev.filter((a) => a.id !== annotation.id));
  }, []);

  return useMemo(
    () => ({
      annotations,
      addAnnotation,
      updateAnnotation,
      removeAnnotation,
    }),
    [annotations, addAnnotation, updateAnnotation, removeAnnotation],
  );
}
